import { Import, ImportDetection, ImportType } from 'dtos/imports/import.dto';
import { PaginatedResponse, toPaginatedResponse } from 'dtos/common/api.dto';
import { apiClient } from 'services/api-client';
import { supabase } from 'services/auth/supabase.client';

export class ImportService {
  static async list(orgId: string, page = 1, pageSize = 8): Promise<PaginatedResponse<Import>> {
    const response = await apiClient.get<Import[]>(`/orgs/${orgId}/imports?page=${page}&pageSize=${pageSize}`);
    return toPaginatedResponse(response, page, pageSize);
  }

  // Uploads the file and returns the backend's column detection so the user
  // can review the mapping before processing starts. With `auto`, the backend
  // decides whether the file is a price book, cost file or sales report.
  static async upload(orgId: string, file: File, storeId: string | null, importType: ImportType, auto = false): Promise<{ importRecord: Import; detection: ImportDetection }> {
    const content = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', content);
    const fileHash = Array.from(new Uint8Array(digest)).map(value => value.toString(16).padStart(2, '0')).join('');
    const initiate = await apiClient.post<any>(`/orgs/${orgId}/imports/upload-url`, {
      store_id: storeId, import_type: importType, file_name: file.name, file_size_bytes: file.size, file_hash: fileHash,
    });
    const importRecord = initiate.data.import as Import;
    if (initiate.data.duplicate && importRecord.status !== 'UPLOADED' && importRecord.status !== 'READY') {
      throw new Error(`This exact file was already imported on ${new Date(importRecord.created_at).toLocaleDateString()}. Nothing new to process.`);
    }
    if (!initiate.data.already_uploaded) {
      if (!supabase || !initiate.data.upload_token) throw new Error('File storage is not configured.');
      const { error } = await supabase.storage.from('imports').uploadToSignedUrl(initiate.data.file_key, initiate.data.upload_token, file);
      if (error) throw new Error(error.message);
    }
    if (importRecord.status !== 'READY') await apiClient.post(`/orgs/${orgId}/imports/${importRecord.id}/upload-complete`);
    const detection = await apiClient.post<ImportDetection>(`/orgs/${orgId}/imports/${importRecord.id}/detect`, auto ? { auto: true } : {});
    return { importRecord, detection: detection.data };
  }

  // The owner says the file is a different kind than we guessed.
  static async retype(orgId: string, importId: string, importType: ImportType): Promise<ImportDetection> {
    const detection = await apiClient.post<ImportDetection>(`/orgs/${orgId}/imports/${importId}/detect`, { import_type: importType });
    return detection.data;
  }

  static async confirm(orgId: string, importId: string, mapping: Record<string, string>): Promise<Import> {
    await apiClient.post(`/orgs/${orgId}/imports/${importId}/mapping`, mapping);
    await apiClient.post(`/orgs/${orgId}/imports/${importId}/validate`);
    const confirmed = await apiClient.post<Import>(`/orgs/${orgId}/imports/${importId}/confirm`);
    return confirmed.data;
  }
}
